import { type AirServerError, isAirAdmin } from '@airreading/common';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

import { getCoach, getUser } from '@/lib/api-calls.js';
import { airServerErrorFrom, axiosErrorFrom } from '@/lib/error-handling.js';
import { QueryKeys } from '@/lib/queries.js';

import { InfoPage } from '@/components/info-page.js';
import { InfoPageSuspenseBoundary } from '@/components/info-page-suspense-boundary.js';

import { SsoEditableInfoCard } from '@/components/sso/sso-info-card.js';
import { useToast } from '@/components/ui/hooks/use-toast.js';
import { validateRouteRole } from '@/lib/routing.js';
import { BasicInfoCard } from '../-components/basic-info-card.js';
import { ManagePermissionsDropdown } from '../-components/manage-permissions-dropdown.js';
import { ReadingSetupCard } from '../-components/reading-setup-card.js';
import { coachesTabs } from '../-components/tabs.js';
import { TeachingSectionCard } from '../-components/teaching-section-card.js';

export const Route = createFileRoute('/_layout/teachers/$teacherId/')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: CoachPage,
});

function CoachPage() {
  return (
    <InfoPageSuspenseBoundary
      onError={(e) => {
        const axiosError = axiosErrorFrom(e);
        const airServerError = airServerErrorFrom(e);

        return {
          allowRetry: axiosError?.response?.status !== 404,
          errorHeader: airServerError?.userFacingMessage || 'Failed to load teacher. Please try again.',
        };
      }}
    >
      <CoachDetail />
    </InfoPageSuspenseBoundary>
  );
}

function CoachDetail() {
  const { toast } = useToast();
  const { teacherId: userId } = Route.useParams();
  const { data: user } = useSuspenseQuery({
    queryKey: QueryKeys.getUser(userId),
    queryFn: () => getUser(userId),
  });
  const { data: coach } = useSuspenseQuery(getCoach(userId));

  const onSubmitSsoSuccess = () => {
    toast({ title: 'Teacher info updated' });
  };
  const onSubmitSsoFailure = (error?: AirServerError) => {
    toast({ title: 'Failed to update teacher SSO', variant: 'destructive', description: error?.userFacingMessage });
  };

  return (
    <div className="mb-2">
      <InfoPage
        headerText={user.fullNameOrEmail}
        headerActions={<ManagePermissionsDropdown user={user} />}
        tabs={coachesTabs({ teacherId: userId })}
        breadcrumbs={{
          linkItems: [
            {
              label: 'Teachers',
              link: {
                to: '/teachers',
              },
            },
          ],
          currentPage: user.fullNameOrEmail ?? '',
        }}
      >
        <>
          <BasicInfoCard coach={coach} userId={userId} />
          <SsoEditableInfoCard user={user} onSuccess={onSubmitSsoSuccess} onError={onSubmitSsoFailure} />
          <ReadingSetupCard coach={coach} userId={userId} />
          <TeachingSectionCard coach={coach} userId={userId} />
        </>
      </InfoPage>
    </div>
  );
}
